import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import { EuroWithBackgroundIcon, PenUnderlineIcon } from '@mopla/ui';

import { PaymentDetails } from '../../../organisms/PaymentDetails/PaymentDetails';

interface IPaymentMethodRowProps {
	/** Whether the user has a saved default payment method. */
	hasPaymentMethod: boolean;
	/** Opens the payment-method selection to change/add the method. */
	onEdit: () => void;
}

/**
 * The "Zahlung" row: shows the saved payment method (icon + masked number +
 * expiry via <PaymentDetails/>) with an edit pencil, or a hint when none is set.
 */
export const PaymentMethodRow: FC<IPaymentMethodRowProps> = ({
	hasPaymentMethod,
	onEdit,
}) => {
	const { t } = useTranslation('ticketshop');

	if (!hasPaymentMethod) {
		return (
			<Stack
				direction="row"
				alignItems="center"
				gap={1.5}
				sx={{ py: 1, width: '100%' }}
			>
				<EuroWithBackgroundIcon />
				<Typography variant="l1" color="solutions.600">
					{t('ticket_detail.payment_no_method')}
				</Typography>
			</Stack>
		);
	}

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			gap={1.5}
			sx={{ py: 1, width: '100%' }}
		>
			<PaymentDetails cardLabel={t('payment.credit_card')} />
			<Box
				component="button"
				type="button"
				onClick={onEdit}
				aria-label={t('payment.edit_payment_method')}
				data-testid="operator-ticket-payment-edit-method"
				sx={{
					display: 'flex',
					alignItems: 'center',
					p: 0,
					border: 'none',
					background: 'none',
					cursor: 'pointer',
				}}
			>
				<PenUnderlineIcon />
			</Box>
		</Stack>
	);
};
